import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsAPI } from '../services/api';
import { useMyTenants } from '../hooks/useMyTenants';
import { useAuthStore } from '../stores/authStore';

// [2026-03-21] API 에러 메시지 추출 (429 한도 초과 등)
const getErrorMessage = (error: any): string => {
  const detail = error?.response?.data?.detail;
  if (detail) return detail;
  if (error?.response?.status === 429) return '요청 한도를 초과했습니다.';
  return '요청 처리 중 오류가 발생했습니다.';
};

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // [2026-03-24] 삭제는 슈퍼관리자만
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser);
  const [errorAlert, setErrorAlert] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showQAModal, setShowQAModal] = useState(false);
  const [showCrawlModal, setShowCrawlModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [detailDoc, setDetailDoc] = useState<any>(null);
  const [detailChunks, setDetailChunks] = useState<any[]>([]);
  const [editingDoc, setEditingDoc] = useState<any>(null);
  const [textForm, setTextForm] = useState({ title: '', content: '' });
  const [crawlForm, setCrawlForm] = useState({ url: '', title: '', maxDepth: 1, maxPages: 10 });
  const [editForm, setEditForm] = useState({ title: '', content: '' });
  const [qaForm, setQAForm] = useState({
    title: '',
    qaPairs: [{ question: '', answer: '' }],
  });

  // [2026-03-22] 역할별 테넌트 목록 (슈퍼관리자: 전체, 일반: 내 테넌트)
  const { tenants } = useMyTenants();
  const selectedTenantData = tenants.find((t: any) => t.id === selectedTenant);
  const apiKey = selectedTenantData?.api_key;

  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['documents', selectedTenant],
    queryFn: () => documentsAPI.list(apiKey!, 1, 100),
    enabled: !!apiKey,
  });

  const documents = documentsData?.data?.items || [];

  const uploadMutation = useMutation({
    mutationFn: ({ file, title }: { file: File; title?: string }) =>
      documentsAPI.uploadFile(apiKey!, file, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', selectedTenant] });
      setShowUploadModal(false);
    },
    onError: (error: any) => setErrorAlert(getErrorMessage(error)),
  });

  const createTextMutation = useMutation({
    mutationFn: () => documentsAPI.createText(apiKey!, textForm.title, textForm.content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', selectedTenant] });
      setShowTextModal(false);
      setTextForm({ title: '', content: '' });
    },
    onError: (error: any) => setErrorAlert(getErrorMessage(error)),
  });

  const createQAMutation = useMutation({
    mutationFn: () =>
      documentsAPI.createQA(
        apiKey!,
        qaForm.title,
        qaForm.qaPairs.filter((qa) => qa.question && qa.answer)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', selectedTenant] });
      setShowQAModal(false);
      setQAForm({ title: '', qaPairs: [{ question: '', answer: '' }] });
    },
    onError: (error: any) => setErrorAlert(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => documentsAPI.delete(apiKey!, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', selectedTenant] });
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: (docId: string) => documentsAPI.reprocess(apiKey!, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', selectedTenant] });
    },
  });

  const crawlMutation = useMutation({
    mutationFn: () => documentsAPI.crawl(apiKey!, crawlForm.url, crawlForm.title || undefined, crawlForm.maxDepth, crawlForm.maxPages),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', selectedTenant] });
      setShowCrawlModal(false);
      setCrawlForm({ url: '', title: '', maxDepth: 1, maxPages: 10 });
    },
    onError: (error: any) => setErrorAlert(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: () => documentsAPI.update(apiKey!, editingDoc.id, { title: editForm.title, content: editForm.content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', selectedTenant] });
      setShowEditModal(false);
      setEditingDoc(null);
      setEditForm({ title: '', content: '' });
    },
  });

  const openDetailPanel = async (doc: any) => {
    try {
      const docRes = await documentsAPI.get(apiKey!, doc.id);
      setDetailDoc(docRes.data);
    } catch {
      setDetailDoc(doc);
    }
    try {
      const chunksRes = await documentsAPI.getChunks(apiKey!, doc.id);
      setDetailChunks(chunksRes.data || []);
    } catch {
      setDetailChunks([]);
    }
    setShowDetailPanel(true);
  };

  const openEditModal = async (doc: any) => {
    try {
      // 문서 상세 정보 가져오기
      const response = await documentsAPI.get(apiKey!, doc.id);
      const docDetail = response.data;
      setEditingDoc(docDetail);
      setEditForm({ title: docDetail.title, content: docDetail.content || '' });
      setShowEditModal(true);
    } catch (error) {
      console.error('Failed to fetch document:', error);
      // 실패 시 기존 정보 사용
      setEditingDoc(doc);
      setEditForm({ title: doc.title, content: doc.content || '' });
      setShowEditModal(true);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate({ file });
    }
  };

  const addQAPair = () => {
    setQAForm({
      ...qaForm,
      qaPairs: [...qaForm.qaPairs, { question: '', answer: '' }],
    });
  };

  const removeQAPair = (index: number) => {
    setQAForm({
      ...qaForm,
      qaPairs: qaForm.qaPairs.filter((_, i) => i !== index),
    });
  };

  const updateQAPair = (index: number, field: 'question' | 'answer', value: string) => {
    const newPairs = [...qaForm.qaPairs];
    newPairs[index][field] = value;
    setQAForm({ ...qaForm, qaPairs: newPairs });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      completed: '완료',
      processing: '처리중',
      pending: '대기중',
      failed: '실패',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getSourceTypeBadge = (type: string) => {
    const labels: Record<string, string> = {
      file: '파일',
      text: '텍스트',
      qa: 'Q&A',
      url: 'URL',
    };
    return labels[type] || type;
  };

  return (
    <div className="p-8">
      {/* [2026-03-21] 에러 알림 (429 한도 초과 등) */}
      {errorAlert && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
          <span className="text-red-700">{errorAlert}</span>
          <button onClick={() => setErrorAlert(null)} className="text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">문서 관리</h1>
          <p className="text-gray-600">학습 문서를 관리합니다.</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">테넌트 선택</option>
            {tenants.map((tenant: any) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
          {selectedTenant && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                + 문서 추가
              </button>
            </div>
          )}
        </div>
      </div>

      {!selectedTenant ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          테넌트를 선택하세요.
        </div>
      ) : isLoading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          로딩 중...
        </div>
      ) : documents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          등록된 문서가 없습니다.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  제목
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  타입
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  청크
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  생성일
                </th>
                {/* [2026-03-24] 작업 열: 슈퍼관리자만 */}
                {isSuperuser && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    작업
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documents.map((doc: any) => (
                <tr
                  key={doc.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => openDetailPanel(doc)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{doc.title}</div>
                    {doc.file_name && (
                      <div className="text-sm text-gray-500">{doc.file_name}</div>
                    )}
                    {doc.source_url && (
                      <div className="text-sm text-gray-400 truncate max-w-xs">{doc.source_url}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    <div>{getSourceTypeBadge(doc.source_type)}</div>
                    {doc.file_size > 0 && (
                      <div className="text-xs text-gray-400 mt-1">{formatFileSize(doc.file_size)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(doc.status)}
                    {doc.status === 'failed' && doc.error_message && (
                      <div className="text-xs text-red-500 mt-1 truncate max-w-[150px]" title={doc.error_message}>
                        {doc.error_message}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{doc.chunk_count || 0}</td>
                  <td className="px-6 py-4 text-gray-500">
                    {new Date(doc.created_at).toLocaleDateString('ko-KR')}
                  </td>
                  {/* [2026-03-24] 작업 셀: 슈퍼관리자만 */}
                  {isSuperuser && (
                  <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      {(doc.source_type === 'text' || doc.source_type === 'qa' || doc.source_type === 'url') && (
                        <button
                          onClick={() => openEditModal(doc)}
                          className="text-green-600 hover:text-green-800 text-sm"
                        >
                          수정
                        </button>
                      )}
                      {(doc.status === 'failed' || doc.status === 'pending') && (
                        <button
                          onClick={() => reprocessMutation.mutate(doc.id)}
                          disabled={reprocessMutation.isPending}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          재처리
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (confirm('문서를 삭제하시겠습니까?')) {
                            deleteMutation.mutate(doc.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Type Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-4">문서 추가</h2>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  fileInputRef.current?.click();
                }}
                className="w-full p-4 border-2 border-dashed rounded-lg hover:border-blue-500 hover:bg-blue-50 transition text-left"
              >
                <div className="font-medium">파일 업로드</div>
                <div className="text-sm text-gray-500">
                  PDF, DOCX, TXT, MD 등 문서 파일
                </div>
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setShowTextModal(true);
                }}
                className="w-full p-4 border-2 border-dashed rounded-lg hover:border-blue-500 hover:bg-blue-50 transition text-left"
              >
                <div className="font-medium">텍스트 입력</div>
                <div className="text-sm text-gray-500">직접 텍스트 내용 입력</div>
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setShowQAModal(true);
                }}
                className="w-full p-4 border-2 border-dashed rounded-lg hover:border-blue-500 hover:bg-blue-50 transition text-left"
              >
                <div className="font-medium">Q&A 추가</div>
                <div className="text-sm text-gray-500">질문과 답변 쌍 입력</div>
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setShowCrawlModal(true);
                }}
                className="w-full p-4 border-2 border-dashed rounded-lg hover:border-blue-500 hover:bg-blue-50 transition text-left"
              >
                <div className="font-medium">URL 크롤링</div>
                <div className="text-sm text-gray-500">웹사이트 내용 가져오기</div>
              </button>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Text Input Modal */}
      {showTextModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h2 className="text-lg font-semibold mb-4">텍스트 문서 추가</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  제목 *
                </label>
                <input
                  type="text"
                  value={textForm.title}
                  onChange={(e) => setTextForm({ ...textForm, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="문서 제목"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  내용 *
                </label>
                <textarea
                  value={textForm.content}
                  onChange={(e) => setTextForm({ ...textForm, content: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  rows={10}
                  placeholder="학습시킬 텍스트 내용을 입력하세요."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowTextModal(false);
                  setTextForm({ title: '', content: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                취소
              </button>
              <button
                onClick={() => createTextMutation.mutate()}
                disabled={!textForm.title || !textForm.content || createTextMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {createTextMutation.isPending ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Q&A Input Modal */}
      {showQAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Q&A 추가</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  제목 *
                </label>
                <input
                  type="text"
                  value={qaForm.title}
                  onChange={(e) => setQAForm({ ...qaForm, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Q&A 문서 제목"
                />
              </div>
              <div className="space-y-4">
                {qaForm.qaPairs.map((pair, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm">Q&A #{index + 1}</span>
                      {qaForm.qaPairs.length > 1 && (
                        <button
                          onClick={() => removeQAPair(index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          삭제
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={pair.question}
                        onChange={(e) => updateQAPair(index, 'question', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="질문"
                      />
                      <textarea
                        value={pair.answer}
                        onChange={(e) => updateQAPair(index, 'answer', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        rows={3}
                        placeholder="답변"
                      />
                    </div>
                  </div>
                ))}
                <button
                  onClick={addQAPair}
                  className="w-full p-3 border-2 border-dashed rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 transition"
                >
                  + Q&A 추가
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowQAModal(false);
                  setQAForm({ title: '', qaPairs: [{ question: '', answer: '' }] });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                취소
              </button>
              <button
                onClick={() => createQAMutation.mutate()}
                disabled={
                  !qaForm.title ||
                  !qaForm.qaPairs.some((qa) => qa.question && qa.answer) ||
                  createQAMutation.isPending
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {createQAMutation.isPending ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL Crawl Modal */}
      {showCrawlModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h2 className="text-lg font-semibold mb-4">URL 크롤링</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL *
                </label>
                <input
                  type="url"
                  value={crawlForm.url}
                  onChange={(e) => setCrawlForm({ ...crawlForm, url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  제목 (선택)
                </label>
                <input
                  type="text"
                  value={crawlForm.title}
                  onChange={(e) => setCrawlForm({ ...crawlForm, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="미입력 시 페이지 제목 사용"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    크롤링 깊이
                  </label>
                  <select
                    value={crawlForm.maxDepth}
                    onChange={(e) => setCrawlForm({ ...crawlForm, maxDepth: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value={0}>단일 페이지</option>
                    <option value={1}>1단계 링크</option>
                    <option value={2}>2단계 링크</option>
                    <option value={3}>3단계 링크</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    최대 페이지 수
                  </label>
                  <select
                    value={crawlForm.maxPages}
                    onChange={(e) => setCrawlForm({ ...crawlForm, maxPages: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value={5}>5개</option>
                    <option value={10}>10개</option>
                    <option value={20}>20개</option>
                    <option value={50}>50개</option>
                  </select>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                * 크롤링 깊이가 높을수록 더 많은 페이지를 수집하지만 시간이 오래 걸립니다.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCrawlModal(false);
                  setCrawlForm({ url: '', title: '', maxDepth: 1, maxPages: 10 });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                취소
              </button>
              <button
                onClick={() => crawlMutation.mutate()}
                disabled={!crawlForm.url || crawlMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {crawlMutation.isPending ? '크롤링 중...' : '크롤링 시작'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Document Modal */}
      {showEditModal && editingDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">문서 수정</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  제목 *
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="문서 제목"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  내용
                </label>
                <textarea
                  value={editForm.content}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                  rows={15}
                  placeholder="문서 내용"
                />
              </div>
              <p className="text-sm text-gray-500">
                * 내용 수정 시 자동으로 재학습됩니다.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingDoc(null);
                  setEditForm({ title: '', content: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                취소
              </button>
              <button
                onClick={() => updateMutation.mutate()}
                disabled={!editForm.title || updateMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {updateMutation.isPending ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Detail Panel */}
      {showDetailPanel && detailDoc && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black bg-opacity-30"
            onClick={() => { setShowDetailPanel(false); setDetailDoc(null); setDetailChunks([]); }}
          />
          <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{detailDoc.title}</h2>
              <button
                onClick={() => { setShowDetailPanel(false); setDetailDoc(null); setDetailChunks([]); }}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                &times;
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* 기본 정보 */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">기본 정보</h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-gray-400">타입</dt>
                    <dd className="font-medium">{getSourceTypeBadge(detailDoc.source_type)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-400">상태</dt>
                    <dd>{getStatusBadge(detailDoc.status)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-400">청크 수</dt>
                    <dd className="font-medium">{detailDoc.chunk_count || 0}</dd>
                  </div>
                  {detailDoc.file_size > 0 && (
                    <div>
                      <dt className="text-gray-400">파일 크기</dt>
                      <dd className="font-medium">{formatFileSize(detailDoc.file_size)}</dd>
                    </div>
                  )}
                  {detailDoc.file_name && (
                    <div className="col-span-2">
                      <dt className="text-gray-400">파일명</dt>
                      <dd className="font-medium break-all">{detailDoc.file_name}</dd>
                    </div>
                  )}
                  {detailDoc.source_url && (
                    <div className="col-span-2">
                      <dt className="text-gray-400">소스 URL</dt>
                      <dd>
                        <a
                          href={detailDoc.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline break-all"
                        >
                          {detailDoc.source_url}
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* 처리 정보 */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">처리 정보</h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-gray-400">생성일</dt>
                    <dd>{new Date(detailDoc.created_at).toLocaleString('ko-KR')}</dd>
                  </div>
                  {detailDoc.processed_at && (
                    <div>
                      <dt className="text-gray-400">처리 완료</dt>
                      <dd>{new Date(detailDoc.processed_at).toLocaleString('ko-KR')}</dd>
                    </div>
                  )}
                  {detailDoc.updated_at && (
                    <div>
                      <dt className="text-gray-400">최종 수정</dt>
                      <dd>{new Date(detailDoc.updated_at).toLocaleString('ko-KR')}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* 에러 메시지 */}
              {detailDoc.error_message && (
                <div>
                  <h3 className="text-sm font-medium text-red-500 mb-2">오류 메시지</h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    {detailDoc.error_message}
                  </div>
                </div>
              )}

              {/* 메타데이터 */}
              {detailDoc.extra_metadata && Object.keys(detailDoc.extra_metadata).length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-3">메타데이터</h3>
                  <dl className="text-sm space-y-2">
                    {detailDoc.extra_metadata.pages_crawled != null && (
                      <div>
                        <dt className="text-gray-400 inline">크롤링 페이지:</dt>
                        <dd className="inline ml-1 font-medium">{detailDoc.extra_metadata.pages_crawled}개</dd>
                      </div>
                    )}
                    {detailDoc.extra_metadata.max_depth != null && (
                      <div>
                        <dt className="text-gray-400 inline">크롤링 깊이:</dt>
                        <dd className="inline ml-1 font-medium">{detailDoc.extra_metadata.max_depth}</dd>
                      </div>
                    )}
                    {detailDoc.extra_metadata.crawled_urls && (
                      <div>
                        <dt className="text-gray-400 mb-1">수집된 URL</dt>
                        <dd className="space-y-1">
                          {detailDoc.extra_metadata.crawled_urls.map((url: string, i: number) => (
                            <a
                              key={i}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-blue-600 hover:underline text-xs truncate"
                            >
                              {url}
                            </a>
                          ))}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {/* 콘텐츠 미리보기 */}
              {detailDoc.content && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">콘텐츠 미리보기</h3>
                  <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap max-h-80 overflow-y-auto font-mono">
                    {detailDoc.content.length > 2000
                      ? detailDoc.content.substring(0, 2000) + '\n\n... (이하 생략)'
                      : detailDoc.content}
                  </div>
                </div>
              )}

              {/* 청크 분석 결과 */}
              {detailChunks.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-3">
                    분석 결과 ({detailChunks.length}개 청크)
                  </h3>
                  <div className="space-y-3">
                    {detailChunks.map((chunk: any) => (
                      <div key={chunk.id} className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-100 px-3 py-1.5 flex justify-between text-xs text-gray-500">
                          <span>청크 #{chunk.chunk_index + 1}</span>
                          <span>{chunk.token_count} 토큰</span>
                        </div>
                        <div className="px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                          {chunk.content.length > 500
                            ? chunk.content.substring(0, 500) + '...'
                            : chunk.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* [2026-03-24] 하단 액션: 슈퍼관리자만 수정/삭제 */}
              {isSuperuser && (
              <div className="flex gap-2 pt-2 border-t">
                {(detailDoc.source_type === 'text' || detailDoc.source_type === 'qa' || detailDoc.source_type === 'url') && (
                  <button
                    onClick={() => {
                      setShowDetailPanel(false);
                      openEditModal(detailDoc);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm transition"
                  >
                    수정
                  </button>
                )}
                {(detailDoc.status === 'failed' || detailDoc.status === 'pending') && (
                  <button
                    onClick={() => {
                      reprocessMutation.mutate(detailDoc.id);
                      setShowDetailPanel(false);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition"
                  >
                    재처리
                  </button>
                )}
                <button
                  onClick={() => {
                    if (confirm('문서를 삭제하시겠습니까?')) {
                      deleteMutation.mutate(detailDoc.id);
                      setShowDetailPanel(false);
                      setDetailDoc(null);
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm transition"
                >
                  삭제
                </button>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        accept=".pdf,.docx,.doc,.txt,.md,.csv,.xlsx,.xls"
        className="hidden"
      />
    </div>
  );
}
